import React, { Component } from "react";
import * as PropTypes from "prop-types";
import { Pagination } from "antd";

import UIEmptyState from "../../../components/EmptyState";

import TableRow from "./row";

const groupConsecutive = (columns, key) => columns.reduce((blocks, column) => {
	const last = blocks[blocks.length - 1];

	if( column[key] && last && last.key === column[key] )
	{
		last.columns.push(column);
		last.width += column.width;
	}
	else
	{
		blocks.push({ key: column[key], columns: [column], width: column.width });
	}

	return blocks;
}, []);

class Table extends Component {
	static propTypes = {
		onPaginationChange: PropTypes.func,
		data              : PropTypes.array,
		pagination        : PropTypes.object,
		columns           : PropTypes.arrayOf(
			PropTypes.shape({
				title    : PropTypes.any,
				group    : PropTypes.string,
				subgroup : PropTypes.string,
				className: PropTypes.string,
				width    : PropTypes.number.isRequired,
				visible  : PropTypes.bool,
				render   : PropTypes.func.isRequired,
				total    : PropTypes.func,
			}),
		),
	};

	static defaultProps = {
		onPaginationChange: () => null,
		data              : [],
		pagination        : {},
		columns           : [],
	};

	state = {
		canScrollX: false,
	};

	componentDidMount() {
		window.addEventListener("resize", this.checkScrollX);
		this.checkScrollX();
	}

	componentDidUpdate() {
		this.checkScrollX();
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.checkScrollX);
	}

	checkScrollX = () => {
		if( !this.scroller ) return;

		const canScrollX = this.scroller.scrollWidth > this.scroller.clientWidth + 1;

		if( canScrollX !== this.state.canScrollX )
		{
			this.setState({ canScrollX });
		}
	};

	cellProps = (column, extraClassName = "") => ({
		className: `time-card-grid-cell ${column.className || ""} ${extraClassName}`,
		style    : {
			width   : column.width,
			minWidth: column.width,
			maxWidth: column.width,
		},
	});

	_renderHeadCell = (column, index) => (
		<div key={index} {...this.cellProps(column, "time-card-grid-head-cell")}>
			{column.title}
		</div>
	);

	_renderHeader = (columns) => (
		<div className="time-card-grid-header">
			{groupConsecutive(columns, "group").map((block, index) => {
				if( !block.key )
				{
					return this._renderHeadCell(block.columns[0], index);
				}

				return (
					<div key={index} className="time-card-grid-group" style={{ width: block.width, minWidth: block.width }}>
						<div className="time-card-grid-group-title">{block.key}</div>
						<div className="time-card-grid-group-columns">
							{groupConsecutive(block.columns, "subgroup").map((subBlock, sub_index) => subBlock.key ? (
								<div key={sub_index} className="time-card-grid-subgroup" style={{ width: subBlock.width, minWidth: subBlock.width }}>
									<div className="time-card-grid-subgroup-title">{subBlock.key}</div>
									<div className="time-card-grid-group-columns">
										{subBlock.columns.map(this._renderHeadCell)}
									</div>
								</div>
							) : subBlock.columns.map((column, col_index) => this._renderHeadCell(column, `${sub_index}-${col_index}`)))}
						</div>
					</div>
				);
			})}
		</div>
	);

	_renderTotal = (columns) => {
		const firstTotal = columns.findIndex((column) => typeof column.total === "function");

		if( firstTotal < 1 ) return null;

		const [labelColumn, ...fillerColumns] = columns.slice(0, firstTotal);
		const fillerWidth = fillerColumns.reduce((sum, column) => sum + column.width, 0);

		return (
			<div className="time-card-grid-row time-card-grid-total">
				<div {...this.cellProps(labelColumn, "time-card-grid-total-label")}>
					<span>Total do período</span>
				</div>
				{fillerWidth > 0 && <div className="time-card-grid-cell" style={{ width: fillerWidth, minWidth: fillerWidth, maxWidth: fillerWidth }} />}
				{columns.slice(firstTotal).map((column, index) => (
					<div key={index} {...this.cellProps(column)}>
						{column.total ? column.total() : null}
					</div>
				))}
			</div>
		);
	};

	render() {
		const { data } = this.props;

		if( !data.length )
		{
			return (
				<UIEmptyState
					icon={<i className="fal fa-calendar-times" />}
					title="Nenhum registro encontrado"
					description="Não há registros de ponto para o funcionário no período selecionado."
				/>
			);
		}

		const columns = this.props.columns.filter(column => !column.hasOwnProperty("visible") || column.visible);

		return (
			<div className="time-card-table">
				<div ref={el => this.scroller = el} className="time-card-grid">
					{this._renderHeader(columns)}
					<div className="time-card-grid-body">
						{data.map((item, index) => (
							<TableRow
								key={item.id}
								index={index}
								columns={columns}
								updateRow={this.props.updateRow}
								reloadRow={this.props.reloadRow}
								item={item}
								dataDia={item.data}
								fetchGetAll={this.props.fetchGetAll}
								fetchGetItems={this.props.fetchGetItems}
								dataInicial={this.props.dataInicial}
								dataFinal={this.props.dataFinal}
								newJoinColumn={this.props.newJoinColumn}
								removeBatida={this.props.removeBatida}
								journeyOpen={this.props.journeyOpen}
							/>
						))}
						{this._renderTotal(columns)}
					</div>
				</div>
				{this.state.canScrollX && (
					<p className="time-card-table-hint">
						<i className="fal fa-long-arrow-left" />
						Role horizontalmente para ver todas as colunas
						<i className="fal fa-long-arrow-right" />
					</p>
				)}
				<Pagination
					{...this.props.pagination}
					showSizeChanger
					onChange={this.props.onPaginationChange}
					showTotal={(total, range) => `${range[0]}-${range[1]} de ${total} registros`}
				/>
			</div>
		);
	}
}

export default Table;
